import { useCallback, useEffect } from 'react';
import { Path, useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { AfterDeath } from '@/types/schema';

import {
  DiagramDispositionsFormRecipient,
  useDispositiveDiagramSyncContext,
} from '../../contexts/DispositiveDiagramSync.context';
import {
  enrichRecipientForDiagram,
  useBootstrapDiagramSelectedNode,
} from '../../contexts/DispositiveDiagramSync.helpers';
import { DispositiveProvisionsFormRecipient } from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { useRecipientIdMetadataMap } from '../../DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DraggableListItemRecipient.utils';
import { DispositiveProvisionsFormV2Shape } from '../DispositiveProvisionsFormV2.types';

/**
 * Sync the DP form's recipients into the shared diagram sync context so
 * the split-screen diagram previews unsaved edits.
 */
export function useSyncDiagram() {
  const { control, formState } =
    useFormContext<DispositiveProvisionsFormV2Shape>();
  const { setDiagramDispositionsFormState, selectedNode, setSelectedNode } =
    useDispositiveDiagramSyncContext();

  const { id, isEditingRootTarget, isTestamentaryEntity } =
    useDispositiveProvisionsContext();

  const idMetadataMap = useRecipientIdMetadataMap();

  const firstDeathRecipients = useWatch({
    control,
    name: 'dispositiveProvisionsV2.firstDeath.recipients' as const satisfies Path<DispositiveProvisionsFormV2Shape>,
  });
  const secondDeathRecipients = useWatch({
    control,
    name: 'dispositiveProvisionsV2.secondDeath.recipients' as const satisfies Path<DispositiveProvisionsFormV2Shape>,
  });

  useBootstrapDiagramSelectedNode({
    entityId: id,
    defaultAfterDeath: isTestamentaryEntity
      ? AfterDeath.First
      : AfterDeath.None,
    selectedNode,
    setSelectedNode,
  });

  const enrich = useCallback(
    (r: DispositiveProvisionsFormRecipient) =>
      enrichRecipientForDiagram(r, idMetadataMap[r.recipientId ?? '']),
    [idMetadataMap]
  );

  useEffect(() => {
    if (formState.isSubmitting) return;
    if (formState.isLoading) return;
    // Diagram overlay describes the root entity only.
    if (!isEditingRootTarget) return;
    // Freeze the root overlay while a non-root tile is selected.
    if (selectedNode && selectedNode.pulid !== id) return;

    setDiagramDispositionsFormState({
      firstDeath: firstDeathRecipients
        .map(enrich)
        .filter((x): x is DiagramDispositionsFormRecipient => x !== null),
      secondDeath:
        secondDeathRecipients
          ?.map(enrich)
          .filter((x): x is DiagramDispositionsFormRecipient => x !== null) ??
        null,
    });
  }, [
    enrich,
    firstDeathRecipients,
    secondDeathRecipients,
    setDiagramDispositionsFormState,
    isEditingRootTarget,
    formState.isSubmitting,
    formState.isLoading,
    selectedNode,
    id,
  ]);
}
